import { useState } from "react";
import { Button, Card, MultiSelect, Select, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LANGUAGE_CATALOG, languageName } from "@mip-tg-bot-turntable/platform-api";
import { adminErrText, adminSend, AdminApiError } from "../../api.ts";

export function LanguagesTab(props: {
  readonly botId: string;
  readonly languages: readonly string[];
  readonly defaultLanguage: string;
}) {
  const { botId } = props;
  const qc = useQueryClient();
  // 本地编辑态：en-IN 始终包含（兜底，不可移除）。
  const [langs, setLangs] = useState<string[]>(() =>
    props.languages.includes("en-IN") ? [...props.languages] : ["en-IN", ...props.languages],
  );
  const [defaultLang, setDefaultLang] = useState<string>(props.defaultLanguage || "en-IN");

  // 语言配置独立保存（与基础设置 / 转盘分开）。dirty 时高亮提示，避免漏点保存。
  const dirty =
    [...langs].sort().join(",") !== [...props.languages].sort().join(",") ||
    defaultLang !== props.defaultLanguage;

  const save = useMutation({
    mutationFn: () =>
      adminSend("PATCH", `/bots/${botId}`, {
        languages: langs,
        defaultLanguage: langs.includes(defaultLang) ? defaultLang : "en-IN",
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已保存语言配置，已通知 Bot 热加载" });
      void qc.invalidateQueries({ queryKey: ["bot", botId] });
      void qc.invalidateQueries({ queryKey: ["bots"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <div>
          <Text fw={600} size="lg">
            语言配置
          </Text>
          <Text size="xs" c="dimmed">
            勾选此 Bot 支持哪些语言。Mini App 切换器与各按语言输入的 UI（奖品 / 文案 / 命令 /
            推送）都以此为准；未勾选的语言不展示，用户落到 en-IN 兜底。
          </Text>
        </div>
        <MultiSelect
          label="支持语言"
          description="en-IN 为兜底语言，始终启用、不可移除"
          data={LANGUAGE_CATALOG.map((l) => ({ value: l.code, label: `${l.name}（${l.code}）` }))}
          value={langs}
          onChange={(v) => setLangs(v.includes("en-IN") ? v : ["en-IN", ...v])}
          searchable
        />
        <Select
          label="默认语言"
          description="须为已启用的语言之一"
          data={langs.map((c) => ({ value: c, label: languageName(c) }))}
          value={langs.includes(defaultLang) ? defaultLang : "en-IN"}
          onChange={(v) => setDefaultLang(v ?? "en-IN")}
        />
        {dirty ? (
          <Text size="xs" c="orange">
            语言改动尚未保存 —— 点下方「保存语言配置」才会生效（与基础设置 / 转盘是分开保存的）
          </Text>
        ) : null}
        <Button
          onClick={() => save.mutate()}
          loading={save.isPending}
          color={dirty ? "orange" : "blue"}
          variant={dirty ? "filled" : "light"}
          style={{ alignSelf: "flex-start" }}
        >
          {dirty ? "保存语言配置（未保存）" : "保存语言配置"}
        </Button>
      </Stack>
    </Card>
  );
}
